import React, { useState } from "react";
import { IMAGES, PROJECT_TYPES, BUDGET_RANGES } from "../mock";
import { Kicker } from "../components/UI";
import { PageHero } from "./FindRenovate";
import { Check, ArrowRight, Home, Hammer } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { api } from "../lib/api";

const initial = { intent: "", name: "", email: "", phone: "", city: "", location: "", projectType: "", budget: "", timeline: "", message: "" };

export default function Contact() {
  const ref = useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.intent) {
      toast({ title: "Please complete the required fields", description: "Name, email, and your starting point are needed." });
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", form);
      setSubmitted(true);
      toast({ title: "Project inquiry received", description: "We'll be in touch shortly to start the conversation." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again, or email us directly at rselsor56@gmail.com." });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-navy/20 py-3 font-sans text-navy placeholder:text-charcoal/40 focus:border-gold focus:outline-none transition-colors duration-300";
  const labelCls = "font-sans text-[11px] tracking-wide-nav uppercase text-charcoal/60 mb-2 block";

  return (
    <div ref={ref}>
      <Toaster />
      <PageHero
        kicker="Start Your Project"
        title={<>Tell Us What You're <span className="text-gold-soft italic">Envisioning.</span></>}
        sub="Whether you're searching for a fixer-upper or ready to renovate your current home, the first step is a conversation."
        image={IMAGES.bedroom2}
        imageKey="hero_contact"
      />

      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {submitted ? (
            <div className="text-center py-16 reveal">
              <div className="w-20 h-20 mx-auto border-2 border-gold flex items-center justify-center mb-8"><Check size={40} className="text-gold" strokeWidth={1.5} /></div>
              <h2 className="font-serif text-4xl md:text-5xl text-navy">Thank You, {form.name.split(" ")[0]}.</h2>
              <p className="font-sans text-lg text-charcoal/65 mt-5 font-light max-w-lg mx-auto">Your project inquiry has been received. Our team will reach out soon to learn more about your vision and next steps.</p>
              <button onClick={() => { setForm(initial); setSubmitted(false); }} className="mt-9 inline-flex items-center gap-3 font-sans text-[12px] tracking-wide-nav uppercase text-navy link-underline">Submit Another Inquiry <ArrowRight size={16} className="text-gold" /></button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-10">
              <div className="reveal">
                <Kicker>What Can We Help You With?</Kicker>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {[{ v: "buy", icon: Home, t: "I'm looking to buy a home and renovate it" }, { v: "own", icon: Hammer, t: "I currently own a home I'd like to renovate" }].map((o) => (
                    <button type="button" key={o.v} onClick={() => set("intent", o.v)} className={`flex items-start gap-4 p-6 border text-left transition-all duration-300 ${form.intent === o.v ? "border-gold bg-white" : "border-navy/15 hover:border-navy/40"}`}>
                      <o.icon size={24} strokeWidth={1.5} className={form.intent === o.v ? "text-gold" : "text-navy"} />
                      <span className="font-sans text-sm text-navy leading-snug">{o.t}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 reveal">
                <div><label className={labelCls}>Name *</label><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" /></div>
                <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></div>
                <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 000-0000" /></div>
                <div><label className={labelCls}>Current City</label><input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Where you live now" /></div>
              </div>

              <div className="reveal">
                <label className={labelCls}>Where are you looking to buy / Where is the property located?</label>
                <input className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Neighborhood, city, or area" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 reveal">
                <div>
                  <label className={labelCls}>Which best describes your project?</label>
                  <select className={inputCls} value={form.projectType} onChange={(e) => set("projectType", e.target.value)}>
                    <option value="">Select one</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Estimated Project Budget</label>
                  <select className={inputCls} value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                    <option value="">Select a range</option>
                    {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>

              <div className="reveal">
                <label className={labelCls}>Desired Timeline</label>
                <input className={inputCls} value={form.timeline} onChange={(e) => set("timeline", e.target.value)} placeholder="e.g. Within 6 months" />
              </div>

              <div className="reveal">
                <label className={labelCls}>Tell us about your project</label>
                <textarea rows={4} className={inputCls + " resize-none"} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="What are you hoping to accomplish?" />
              </div>

              <div className="reveal">
                <button type="submit" disabled={sending} className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-navy text-cream font-sans text-[12px] tracking-wide-nav uppercase hover:bg-gold hover:text-navy transition-all duration-400 disabled:opacity-60 disabled:cursor-not-allowed">
                  {sending ? "Sending\u2026" : "Start My Project"} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
